import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { type TransferSearchTransfersData, type UnifiedTransfer } from '@column/api';
import { Dropdown, Icon, formatNumber } from '@column/column-ui-kit';
import { type DataTableColumnDef, Timestamp } from '@column/ui';

import { PopoverFilterAmountData, PopoverFilterTransferDateData, PopoverFilterTransferTypeData } from '~/components';
import { useDebounce } from '~/hooks/useDebounce';
import { useQueryParams } from '~/hooks/useQueryParams';
import { TransferFilterType } from '~/hooks/useTransfers';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { TransferEntryType, convertTransferAmount } from '~/repositories';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { Flex } from '~/styles';
import { formatDateTime, getFormattedDate } from '~/util';

import { TransferAmount } from './amount';
import { TransferDirectionBadge } from './direction';
import { TransferStatusBadge } from './status';
import { TransferTypeBadge } from './utils';

// Maps the camelCase filter keys we use throughout the dashboard to the
// snake_case `query` parameters expected by @column/api's transfer search.
const FILTER_KEY_TO_API: Partial<Record<keyof TransferFilterType, string>> = {
  bankAccountId: 'bank_account_id',
  senderBankAccountId: 'sender_bank_account_id',
  receiverBankAccountId: 'receiver_bank_account_id',
  accountNumberId: 'account_number_id',
  senderAccountNumberId: 'sender_account_number_id',
  receiverAccountNumberId: 'receiver_account_number_id',
  counterpartyId: 'counterparty_id',
  transferBatchId: 'transfer_batch_id',
  transferId: 'transfer_id',
  isIncoming: 'is_incoming',
  amountGt: 'amount_gt',
  amountGte: 'amount_gte',
  amountLt: 'amount_lt',
  amountLte: 'amount_lte',
  createdAtGt: 'created_at_gt',
  createdAtGte: 'created_at_gte',
  createdAtLt: 'created_at_lt',
  createdAtLte: 'created_at_lte',
};

export const toTransferApiQuery = (filters: TransferFilterType): NonNullable<TransferSearchTransfersData['query']> => {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue;
    if (key === 'page') continue;
    out[FILTER_KEY_TO_API[key as keyof TransferFilterType] ?? key] = value;
  }
  return out as NonNullable<TransferSearchTransfersData['query']>;
};

interface TransferTableColumnsOptions {
  bankAccountId?: string;
  timeFormat: TableTimeFormat;
  onTimeFormatChange: (value: TableTimeFormat) => void;
}

interface TransferAmountDescriptionProps {
  amount: number;
  description?: string;
  isAdding?: boolean;
}

const TransferAmountDescription: React.FC<TransferAmountDescriptionProps> = ({ amount, description, isAdding }) => (
  <div className="flex w-full min-w-0 max-w-full items-center gap-2 py-1">
    <TransferAmount $isAdding={isAdding}>{formatNumber(amount)}</TransferAmount>
    {description && <span className="min-w-0 truncate text-xs font-normal text-muted-foreground">{description}</span>}
  </div>
);

interface TransferCreatedAtTimestampProps {
  createdAt?: Date | string;
  timeFormat: TableTimeFormat;
}

const TransferCreatedAtTimestamp: React.FC<TransferCreatedAtTimestampProps> = ({ createdAt, timeFormat }) => {
  if (!createdAt) {
    return null;
  }

  const date = createdAt instanceof Date ? createdAt : new Date(createdAt);
  return (
    <Timestamp
      date={date}
      align="right"
      className="text-xs text-muted-foreground"
      formatHover={(hoverDate) =>
        formatDateTime(hoverDate, { timeZoneFormat: timeFormat === TableTimeFormat.Relative ? 'local' : 'utc' })
      }
    >
      {timeFormat === TableTimeFormat.Relative
        ? getFormattedDate(date, 'time-passed')
        : formatDateTime(date, { timeZoneFormat: 'local' })}
    </Timestamp>
  );
};

export const transferTableColumns = ({
  bankAccountId,
  timeFormat,
  onTimeFormatChange,
}: TransferTableColumnsOptions): DataTableColumnDef<UnifiedTransfer>[] => [
  {
    header: 'Amount / Description',
    accessor: 'description',
    width: 'minmax(300px, auto)',
    className: 'min-w-0',
    cell: ({ row }) => {
      const type = row.type as TransferEntryType;
      const convertAmount = convertTransferAmount(type, Boolean(row.is_incoming));
      let description = row.description ?? '';
      const checkNumber = row.auxiliary_on_us ?? row.check_number;
      if ((row.type === 'check_debit' || row.type === 'check_credit') && checkNumber) {
        description = `#${checkNumber}${description ? ` ${description}` : ''}`;
      }
      const isAdding =
        row.type === 'book'
          ? bankAccountId
            ? row.sender_internal_account?.bank_account_id !== bankAccountId
            : undefined
          : convertAmount !== -1;
      return <TransferAmountDescription amount={row.amount ?? 0} description={description} isAdding={isAdding} />;
    },
  },
  {
    header: 'Type',
    accessor: 'type',
    width: 'minmax(min-content, 150px)',
    cell: ({ value }) => {
      return <TransferTypeBadge type={value as TransferEntryType} />;
    },
  },
  {
    header: 'Status',
    accessor: 'status',
    width: 'minmax(min-content, 150px)',
    cell: ({ row, value }) => {
      const type = row.type as TransferEntryType;
      return <TransferStatusBadge status={value} type={type} />;
    },
  },
  {
    header: 'Direction',
    accessor: 'is_incoming',
    width: 'minmax(min-content, 150px)',
    cell: ({ row }) => {
      const type = row.type as TransferEntryType;
      const isIncoming = Boolean(row.is_incoming);
      return <TransferDirectionBadge type={type} isIncoming={isIncoming} />;
    },
  },
  {
    accessor: 'created_at',
    header: (
      <Dropdown
        size="small"
        positionRight
        active={timeFormat}
        options={[
          { label: 'Relative Time', value: TableTimeFormat.Relative },
          { label: 'Date / Time', value: TableTimeFormat.Absolute },
        ]}
        onChange={onTimeFormatChange}
        customLabel={
          <Flex alignItems="center" gap={4}>
            Created <Icon.ChevronDown />
          </Flex>
        }
      />
    ),
    width: 'min-content',
    cell: ({ row }) => {
      return <TransferCreatedAtTimestamp createdAt={row.created_at} timeFormat={timeFormat} />;
    },
  },
];

interface UseTransfersFiltersOptions {
  bankAccountId?: string;
  defaultPageSize: number;
  isTransferSearchEnabled: boolean;
}

export const useTransfersFilters = ({
  bankAccountId,
  defaultPageSize,
  isTransferSearchEnabled,
}: UseTransfersFiltersOptions) => {
  const {
    queryParams,
    setQueryParams: setUrlQueryParams,
    getQueryParam: getUrlParam,
  } = useQueryParams<TransferFilterType>();

  const initialQuery = isTransferSearchEnabled ? (getUrlParam('query' as keyof TransferFilterType) ?? '') : '';
  const [queryInput, setQueryInput] = useState<string>(() => initialQuery);
  const [debouncedQueryInput, setDebouncedQueryInput] = useState<string>(() => initialQuery);

  const hasSeededQueryFromUrlRef = useRef<boolean>(initialQuery.length > 0);
  useLayoutEffect(() => {
    if (hasSeededQueryFromUrlRef.current) return;
    if (!isTransferSearchEnabled) return;
    hasSeededQueryFromUrlRef.current = true;
    const urlQuery = getUrlParam('query' as keyof TransferFilterType) ?? '';
    if (urlQuery.length === 0) return;
    setQueryInput(urlQuery);
    setDebouncedQueryInput(urlQuery);
  }, [isTransferSearchEnabled, getUrlParam]);

  const [debouncedApplyQuery, cancelApplyQuery] = useDebounce((value: string) => {
    setDebouncedQueryInput(value);
  }, 300);

  const handleQueryInputChange = useCallback(
    (value: string) => {
      setQueryInput(value);
      debouncedApplyQuery(value);
    },
    [debouncedApplyQuery]
  );

  const [bankAccountIdFilterData, setBankAccountIdFilterData] = useState<string | undefined>(() => {
    if (bankAccountId) return undefined;
    if (!queryParams) return undefined;
    const { bankAccountId: queryBankAccountId } = queryParams;
    return queryBankAccountId && queryBankAccountId.length > 0 ? queryBankAccountId : undefined;
  });

  const [amountFilterData, setAmountFilterData] = useState<PopoverFilterAmountData | undefined>(() => {
    if (!queryParams) return undefined;
    const { amount, amountGte, amountLte } = queryParams;
    return {
      amount: amount ? Number(amount) : undefined,
      amountGte: amountGte ? Number(amountGte) : undefined,
      amountLte: amountLte ? Number(amountLte) : undefined,
    };
  });
  const isAmountFilterActive = useMemo(
    () =>
      Boolean(
        (amountFilterData?.amount && amountFilterData.amount > 0) ||
        (amountFilterData?.amountGte && amountFilterData.amountGte > 0) ||
        (amountFilterData?.amountLte && amountFilterData.amountLte > 0)
      ),
    [amountFilterData]
  );

  const [transferTypeFilterData, setTransferTypeFilterData] = useState<PopoverFilterTransferTypeData>(() => {
    if (!queryParams) return {};
    const { isIncoming, type } = queryParams;
    return {
      isIncoming:
        typeof isIncoming !== 'undefined'
          ? typeof isIncoming === 'string'
            ? isIncoming === 'true'
            : isIncoming === true
          : undefined,
      type: type ? (type.split(',') as unknown as PopoverFilterTransferTypeData['type']) : [],
    };
  });
  const isTransferTypeFilterActive = useMemo(
    () =>
      Boolean(
        (transferTypeFilterData?.type && transferTypeFilterData.type.length > 0) ||
        transferTypeFilterData?.isIncoming !== undefined
      ),
    [transferTypeFilterData]
  );

  const [transferStatusFilterData, setTransferStatusFilterData] = useState<string[] | undefined>(() => {
    if (!queryParams) return [];
    const { status } = queryParams;
    return status ? status.split(',') : [];
  });

  const [transferDateFilterData, setTransferDateFilterData] = useState<Partial<PopoverFilterTransferDateData>>(() => {
    if (!queryParams) return {};
    const { createdAtGte, createdAtLt } = queryParams;
    return {
      createdAtGte: createdAtGte && createdAtGte.length > 0 ? createdAtGte : undefined,
      createdAtLt: createdAtLt && createdAtLt.length > 0 ? createdAtLt : undefined,
    };
  });
  const isDateFilterActive = useMemo(
    () => Boolean(transferDateFilterData?.createdAtGte && transferDateFilterData?.createdAtLt),
    [transferDateFilterData]
  );

  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState({
    defaultPageSize,
    initialCursor: queryParams?.startingAfter,
    initialPageIndex: typeof queryParams?.pageIndex === 'number' ? queryParams.pageIndex : 0,
  });

  useEffect(() => {
    setUrlQueryParams((prev) => {
      const next: TransferFilterType = { ...(prev ?? {}) };
      if (isTransferSearchEnabled) {
        next.query = debouncedQueryInput || undefined;
      }
      next.bankAccountId = bankAccountId ? undefined : bankAccountIdFilterData || undefined;
      next.amount =
        amountFilterData?.amount && amountFilterData.amount > 0
          ? (String(amountFilterData.amount) as never)
          : undefined;
      next.amountGte =
        amountFilterData?.amountGte && amountFilterData.amountGte > 0
          ? (String(amountFilterData.amountGte) as never)
          : undefined;
      next.amountLte =
        amountFilterData?.amountLte && amountFilterData.amountLte > 0
          ? (String(amountFilterData.amountLte) as never)
          : undefined;
      next.type =
        transferTypeFilterData.type && transferTypeFilterData.type.length > 0
          ? ((transferTypeFilterData.type as unknown as string[]).join(',') as TransferFilterType['type'])
          : undefined;
      next.isIncoming =
        typeof transferTypeFilterData.isIncoming === 'boolean' ? transferTypeFilterData.isIncoming : undefined;
      next.status =
        transferStatusFilterData && transferStatusFilterData.length > 0
          ? (transferStatusFilterData.join(',') as TransferFilterType['status'])
          : undefined;
      next.createdAtGte = transferDateFilterData?.createdAtGte || undefined;
      next.createdAtLt = transferDateFilterData?.createdAtLt || undefined;
      next.startingAfter = cursor || undefined;
      next.pageIndex = pageIndex > 0 ? pageIndex : undefined;
      return next;
    });
  }, [
    setUrlQueryParams,
    bankAccountId,
    isTransferSearchEnabled,
    debouncedQueryInput,
    bankAccountIdFilterData,
    amountFilterData,
    transferTypeFilterData,
    transferStatusFilterData,
    transferDateFilterData,
    cursor,
    pageIndex,
  ]);

  const filters = useMemo<TransferFilterType>(() => {
    const f: TransferFilterType = {};
    if (bankAccountId) f.bankAccountId = bankAccountId;
    if (isTransferSearchEnabled && debouncedQueryInput) f.query = debouncedQueryInput;
    if (!bankAccountId && bankAccountIdFilterData) f.bankAccountId = bankAccountIdFilterData;
    if (queryParams?.counterpartyId) f.counterpartyId = queryParams.counterpartyId;
    if (queryParams?.accountNumberId) f.accountNumberId = queryParams.accountNumberId;
    if (queryParams?.transferBatchId) f.transferBatchId = queryParams.transferBatchId;
    if (transferTypeFilterData.type && transferTypeFilterData.type.length > 0) {
      f.type = (transferTypeFilterData.type as unknown as string[]).join(',') as TransferFilterType['type'];
    }
    if (transferStatusFilterData && transferStatusFilterData.length > 0) {
      f.status = transferStatusFilterData.join(',') as TransferFilterType['status'];
    }
    if (typeof transferTypeFilterData.isIncoming === 'boolean') f.isIncoming = transferTypeFilterData.isIncoming;
    if (amountFilterData?.amount && amountFilterData.amount > 0) f.amount = String(amountFilterData.amount);
    if (amountFilterData?.amountGte && amountFilterData.amountGte > 0) f.amountGte = String(amountFilterData.amountGte);
    if (amountFilterData?.amountLte && amountFilterData.amountLte > 0) f.amountLte = String(amountFilterData.amountLte);
    if (transferDateFilterData?.createdAtGte) f.createdAtGte = transferDateFilterData.createdAtGte;
    if (transferDateFilterData?.createdAtLt) f.createdAtLt = transferDateFilterData.createdAtLt;
    return f;
  }, [
    bankAccountId,
    isTransferSearchEnabled,
    debouncedQueryInput,
    bankAccountIdFilterData,
    queryParams?.counterpartyId,
    queryParams?.accountNumberId,
    queryParams?.transferBatchId,
    transferTypeFilterData,
    transferStatusFilterData,
    amountFilterData,
    transferDateFilterData,
  ]);

  const isFilterActive = useMemo(
    () =>
      Boolean(
        (isTransferSearchEnabled && queryInput) ||
        (!bankAccountId && bankAccountIdFilterData) ||
        isAmountFilterActive ||
        isTransferTypeFilterActive ||
        (transferStatusFilterData && transferStatusFilterData.length > 0) ||
        isDateFilterActive
      ),
    [
      bankAccountId,
      isTransferSearchEnabled,
      queryInput,
      bankAccountIdFilterData,
      isAmountFilterActive,
      isTransferTypeFilterActive,
      transferStatusFilterData,
      isDateFilterActive,
    ]
  );

  const resetAllFilters = useCallback(() => {
    cancelApplyQuery();
    setBankAccountIdFilterData(undefined);
    setAmountFilterData({ amount: undefined, amountGte: undefined, amountLte: undefined });
    setTransferTypeFilterData({});
    setTransferStatusFilterData([]);
    setTransferDateFilterData({});
    setQueryInput('');
    setDebouncedQueryInput('');
    setUrlQueryParams(() => undefined);
    resetPagination();
  }, [cancelApplyQuery, resetPagination, setUrlQueryParams]);

  return {
    queryInput,
    setQueryInput,
    debouncedQueryInput,
    setDebouncedQueryInput,
    handleQueryInputChange,
    cancelApplyQuery,
    bankAccountIdFilterData,
    setBankAccountIdFilterData,
    amountFilterData,
    setAmountFilterData,
    isAmountFilterActive,
    transferTypeFilterData,
    setTransferTypeFilterData,
    isTransferTypeFilterActive,
    transferStatusFilterData,
    setTransferStatusFilterData,
    transferDateFilterData,
    setTransferDateFilterData,
    isDateFilterActive,
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    resetPagination,
    setPageSize,
    filters,
    isFilterActive,
    resetAllFilters,
    queryParams,
    setUrlQueryParams,
  };
};
